import React, {
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  PropsWithChildren,
} from 'react';
import ReactDOM from 'react-dom';
import styled from 'styled-components';
import { SimpleModalTypes } from './types';
import { SimpleModalProps, SimpleModal } from './SimpleModal';

export type SimpleModalContainerProps = SimpleModalProps & {
  isOpen?: boolean;
  isLoading?: boolean;
  type: SimpleModalTypes;
  title?: React.ReactNode;
  animationDuration?: number;
};

type OverlayProps = {
  isVisible: boolean;
  animationDuration: number;
};

const Overlay = styled.div<OverlayProps>`
  background-color: ${({ theme }) => theme.colors.neutrals.transparentGrays[4]};
  display: flex;
  justify-content: center;
  align-items: center;
  opacity: ${({ isVisible }) => (isVisible ? '1' : '0')};
  position: fixed;
  bottom: 0px;
  transition: opacity
    ${(animationDuration) =>
      `${animationDuration}ms, visibility ${animationDuration}ms`};
  visibility: ${({ isVisible }) => (isVisible ? 'visible' : 'hidden')};
  width: 100vw;
  height: 100vh;
  overflow: auto;
  z-index: 1;
`;

export const SimpleModalContainer: React.FC<
  PropsWithChildren<SimpleModalContainerProps>
> = ({
  isOpen = false,
  animationDuration = 200,
  onClose,
  title = '',
  titlePosition = 'left',
  type,
  children,
}) => {
  const ref = useRef<HTMLElement | null>();
  const [isVisible, setIsVisible] = useState(false);
  const [mounted, setMounted] = useState(false);
  const overlayId = 'modal-overlay';
  useEffect(() => {
    ref.current = document.getElementById('modal-root');
    setMounted(true);
  }, []);

  function preventScroll(isOpen: boolean) {
    setIsVisible(isOpen);
    const overflowStyle = isOpen ? 'hidden' : 'initial';
    document.body.style.overflow = overflowStyle;
    const root = document.documentElement;
    if (root) root.style.overflow = overflowStyle;
  }
  useLayoutEffect(() => {
    preventScroll(isOpen)
    return () => preventScroll(false)
  }, [isOpen]);

  const handleClick = (event?: React.MouseEvent, forceClose = false) => {
    if (forceClose || (event?.target as Element)?.id === overlayId) {
      setIsVisible(false);
      onClose && setTimeout(onClose, animationDuration);
    }
  };

  const handleClose = () => {
    if (type === 'save-search') setIsVisible(true);
    handleClick(undefined, true);
  };

  return mounted && ref.current
    ? ReactDOM.createPortal(
        <Overlay
          animationDuration={animationDuration}
          isVisible={isVisible}
          id={overlayId}
          onClick={handleClick}
        >
          <SimpleModal
            isVisible={isVisible}
            onClose={handleClose}
            title={title}
            titlePosition={titlePosition}
          >
            {children}
          </SimpleModal>
        </Overlay>,
        ref.current
      )
    : null;
};
